/* 即時觀看（規劃書 §9.1）。
 *
 * 從原生版 frontend/dist/live.js 移植，**邏輯與判準完全照搬**——
 * 那些細節是實際踩過坑才有的，不要「簡化」：
 *
 *   1. 成功判準是「video 解出第一張影格」，不是 onTrack。
 *      WebRTC 媒體走 UDP 8189 直連，觀看端在 VPN 外時信令會成功、
 *      onTrack 也會觸發，但 ICE 永遠連不上、畫面全黑。
 *   2. HLS 備援走 <device>-hls 這條路（H.264；HLS 不支援 VP8）。
 *   3. 釋放租約用 keepalive，分頁關閉時請求仍送得出去。
 *   4. 同時只允許一個觀看工作，避免多路推流吃頻寬。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const RENEW_RATIO = 0.5;        // 租約過半就續，不等到最後一刻
const HLS_FALLBACK_MS = 35_000; // WebRTC 這麼久還沒畫面就改 HLS
                                // 真機從 start_stream 到推上來要 26–35 秒
                                // （相機初始化＋編碼器＋WHIP），不能太短

export type LiveKind = 'idle' | 'busy' | 'live' | 'error';

export interface LiveStatus {
  message: string;
  kind: LiveKind;
  /** 目前實際用的傳輸方式，供介面標示與測試判讀 */
  transport: 'none' | 'webrtc' | 'hls';
}

interface SessionResp {
  session_token: string;
  whep_url: string;
  lease_sec: number;
  started_publisher?: boolean;
}

/* vendor 的全域物件（public/vendor 底下，非 ES module） */
declare global {
  interface Window {
    MediaMTXWebRTCReader?: new (opts: {
      url: string;
      token?: string;
      onError?: (e: unknown) => void;
      onTrack?: (e: RTCTrackEvent) => void;
    }) => { close(): void };
    Hls?: {
      new (cfg: Record<string, unknown>): {
        on(ev: string, cb: (e: unknown, d: { fatal: boolean; details?: string; type?: string }) => void): void;
        loadSource(u: string): void;
        attachMedia(v: HTMLVideoElement): void;
        destroy(): void;
      };
      isSupported(): boolean;
      Events: { ERROR: string; MANIFEST_PARSED: string };
    };
  }
}

interface Session {
  token: string | null;
  deviceKey: string;
  video: HTMLVideoElement | null;
  reader: { close(): void } | null;
  hls: { destroy(): void } | null;
  renewTimer: ReturnType<typeof setInterval> | null;
  fallbackTimer: ReturnType<typeof setTimeout> | null;
  /* 等裝置推流時的重試計時器：WHEP／HLS 清單在裝置真正推上來前都會 404 */
  hlsRetry: ReturnType<typeof setTimeout> | null;
  whepRetry: ReturnType<typeof setTimeout> | null;
  gotFrame: boolean;
  fellBack: boolean;
  frameTimer: ReturnType<typeof setInterval> | null;
}

/** 模組層級的「目前工作」，跨元件共用——同時只能有一個。 */
let current: Session | null = null;

function stopCurrent() {
  if (!current) return;
  const s = current;
  current = null;

  if (s.renewTimer) clearInterval(s.renewTimer);
  if (s.fallbackTimer) clearTimeout(s.fallbackTimer);
  if (s.hlsRetry) clearTimeout(s.hlsRetry);
  if (s.whepRetry) clearTimeout(s.whepRetry);
  if (s.frameTimer) clearInterval(s.frameTimer);
  try { s.reader?.close(); } catch { /* 已關閉 */ }
  // HLS 實例不銷毀會繼續抓切片，切裝置後還在背景吃頻寬
  try { s.hls?.destroy(); } catch { /* 已銷毀 */ }

  if (s.video) {
    s.video.srcObject = null;
    s.video.removeAttribute('src');
    try { s.video.load(); } catch { /* 忽略 */ }
  }

  // keepalive：分頁關閉時這個請求仍送得出去
  if (s.token) {
    fetch(`/api/v1/live-sessions/${s.token}`, { method: 'DELETE', keepalive: true })
      .catch(() => { /* 離線也無妨，後端租約到期會自己收割 */ });
  }
}

// 分頁關閉或切到背景時主動釋放，不要讓裝置白推流
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', stopCurrent);
}

export function useLive() {
  const [status, setStatus] = useState<LiveStatus>({
    message: '尚未開始', kind: 'idle', transport: 'none',
  });
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stop = useCallback(() => {
    stopCurrent();
    setStatus({ message: '已停止', kind: 'idle', transport: 'none' });
  }, []);

  /* 元件卸載（例如關閉抽屜）一定要釋放租約 */
  useEffect(() => () => stopCurrent(), []);

  const start = useCallback(async (deviceKey: string) => {
    stopCurrent();

    const session: Session = {
      token: null, deviceKey, video: videoRef.current,
      reader: null, hls: null, renewTimer: null, fallbackTimer: null,
      hlsRetry: null, whepRetry: null,
      gotFrame: false, fellBack: false, frameTimer: null,
    };
    current = session;

    const report = (message: string, kind: LiveKind, transport: LiveStatus['transport'] = 'none') => {
      if (current === session) setStatus({ message, kind, transport });
    };

    report('正在建立觀看工作…', 'busy');

    let sess: SessionResp;
    try {
      const res = await fetch('/api/v1/live-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        report(err.detail || `無法開始觀看（HTTP ${res.status}）`, 'error');
        return;
      }
      sess = await res.json();
    } catch {
      report('無法連線到平台', 'error');
      return;
    }

    // 建立請求已在伺服器完成，若使用者已離開仍須釋放晚到的租約
    if (current !== session) {
      fetch(`/api/v1/live-sessions/${sess.session_token}`,
            { method: 'DELETE', keepalive: true }).catch(() => {});
      return;
    }
    session.token = sess.session_token;

    const video = videoRef.current;
    if (!video) {
      report('播放元件尚未就緒', 'error');
      return;
    }
    session.video = video;
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;        // 聲音由使用者手動開啟（§9.1）

    report(sess.started_publisher ? '已通知裝置推流，等待畫面…' : '接上既有串流…', 'busy');

    /* ── HLS 備援 ── */
    const startHls = () => {
      if (current !== session) return;
      try { session.reader?.close(); } catch { /* 已關閉 */ }
      session.reader = null;
      video.srcObject = null;

      // 清單就緒不等於有畫面。等待真正解碼，且不能無限重試黑畫面。
      const baseline = video.getVideoPlaybackQuality?.().totalVideoFrames || 0;
      const deadline = Date.now() + 60_000;
      let lastFrames = baseline;
      let lastProgress = Date.now();
      session.frameTimer = setInterval(() => {
        if (current !== session) return;
        const frames = video.getVideoPlaybackQuality?.().totalVideoFrames || 0;
        if (frames > lastFrames && video.videoWidth > 0 && video.readyState >= 2) {
          lastFrames = frames;
          lastProgress = Date.now();
          session.gotFrame = true;
          report('即時播放中（HLS）', 'live', 'hls');
        } else if ((!session.gotFrame && Date.now() >= deadline)
                   || (session.gotFrame && Date.now() - lastProgress > 15_000)) {
          report(session.gotFrame ? '影像已中斷，請重新開啟觀看' : '未收到可播放影格，請檢查裝置連線與推流狀態', 'error', 'hls');
          stopCurrent();
        }
      }, 500);

      // HLS 路徑有兩種可能，依發布端而定：
      //   live/<id>-hls  模擬器：主路徑推 VP8、另開一條 H.264 給 HLS
      //   live/<id>      真機：本來就推 H.264，沒有額外的 -hls 路徑
      // 寫死 -hls 會讓真機一直 404（HLS 不支援 VP8，所以模擬器才需要分流）。
      // 依序試，哪條先成功就用哪條。
      const q = `?token=${encodeURIComponent(sess.session_token)}`;
      const candidates = [
        `/live/${encodeURIComponent(deviceKey)}/index.m3u8${q}`,       // 主路徑先（真機只有這一條 H.264）
        `/live/${encodeURIComponent(deviceKey)}-hls/index.m3u8${q}`,   // 模擬器的 -hls 副路徑
      ];
      let candIdx = 0;
      const url = candidates[candIdx];

      // Safari 原生支援 HLS，其餘靠 hls.js
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.play().catch(() => {});
        report('等待 HLS 影格…', 'busy', 'hls');
        return;
      }
      if (!window.Hls?.isSupported()) {
        report('此瀏覽器無法播放，請改用 Chrome／Edge／Safari', 'error');
        return;
      }

      const hls = new window.Hls({
        lowLatencyMode: true,
        backBufferLength: 10,
        manifestLoadingMaxRetry: 6,
        manifestLoadingRetryDelay: 1500,
      });
      session.hls = hls;
      hls.on(window.Hls.Events.ERROR, (_e, data) => {
        if (current !== session || !data.fatal) return;
        // 串流還沒就緒時清單會 404，等裝置推起來會自己好
        if (data.details === 'manifestLoadError' || data.details === 'manifestLoadTimeOut') {
          // 先換下一條候選路徑（-hls 不存在時試主路徑），
          // 都試過了才進入等待——否則真機會卡在永遠 404 的 -hls 上。
          if (candIdx + 1 < candidates.length) {
            candIdx += 1;
            hls.loadSource(candidates[candIdx]);
            return;
          }
          report('等待裝置開始推流…', 'busy', 'hls');
          // 裝置要十幾秒才會真的推上來，清單這時還不存在。
          // hls.js 用完重試次數就不再自己試，得由我們重新載入。
          if (session.hlsRetry) clearTimeout(session.hlsRetry);
          session.hlsRetry = setTimeout(() => {
            if (current !== session) return;
            candIdx = 0;
            hls.loadSource(candidates[0]);
          }, 3000);
          return;
        }
        report(`串流錯誤：${data.details || data.type}`, 'error', 'hls');
      });
      hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
        if (current !== session) return;
        video.play().catch(() => {});
        report('播放清單已就緒，等待 HLS 影格…', 'busy', 'hls');
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      report('改用 HLS 播放…', 'busy', 'hls');
    };

    const fallBack = () => {
      if (current !== session || session.fellBack || session.gotFrame) return;
      session.fellBack = true;
      if (session.fallbackTimer) clearTimeout(session.fallbackTimer);
      startHls();
    };
    session.fallbackTimer = setTimeout(fallBack, HLS_FALLBACK_MS);

    /* ── WebRTC（第一張影格才算成功）── */
    const onFirstFrame = () => {
      if (current !== session || session.fellBack || session.gotFrame) return;
      if (!(video.videoWidth > 0)) return;
      session.gotFrame = true;
      if (session.fallbackTimer) clearTimeout(session.fallbackTimer);
      report('即時播放中', 'live', 'webrtc');
    };
    video.addEventListener('loadeddata', onFirstFrame);
    video.addEventListener('resize', onFirstFrame);

    const Reader = window.MediaMTXWebRTCReader;
    if (!Reader) {
      // vendor 腳本沒載到就直接走 HLS，不要卡住
      fallBack();
      return;
    }

    // 抽成具名設定：404 重試時要用同一份設定重建 reader
    type ReaderCfg = ConstructorParameters<NonNullable<Window['MediaMTXWebRTCReader']>>[0];
    const readerCfg: ReaderCfg = {
      url: `${location.origin}${sess.whep_url}`,
      token: sess.session_token,
      onError: (err) => {
        if (current !== session || session.fellBack) return;
        // vendor reader 在 404 ("stream not found") 時已有內建 2 秒重試機制
        // （#handleError → #restartTimeout → #restart → #start）。
        // 這裡只更新狀態訊息，不要 close + new Reader，否則會雙重重試。
        if (String(err).includes('404') || String(err).includes('stream not found')) {
          report('等待裝置開始推流…', 'busy');
          return;
        }
        fallBack();
      },
      onTrack: (evt) => {
        if (current !== session || session.fellBack) return;
        // 這只代表信令完成，媒體不一定到得了；不取消備援計時器
        video.srcObject = evt.streams[0];
        video.play().catch(() => {});
        report('連線中，等待畫面…', 'busy');
      },
    };
    session.reader = new Reader(readerCfg);

    /* ── 租約續期：停止呼叫就等於告訴後端「我離開了」 ── */
    session.renewTimer = setInterval(async () => {
      if (current !== session) return;
      try {
        const r = await fetch(`/api/v1/live-sessions/${sess.session_token}/renew`,
                              { method: 'POST' });
        if (current === session && !r.ok) {
          report('觀看租約已失效', 'error');
          stopCurrent();
        }
      } catch {
        report('續租失敗，連線可能中斷', 'error');
      }
    }, Math.max(5, Math.floor(sess.lease_sec * RENEW_RATIO)) * 1000);
  }, []);

  return { videoRef, status, start, stop };
}
